import getWindow from 'Utils/get-window';

import {
  deriveUsPrivacyString,
  GPP_SECTION_USNAT,
  UsnatFields,
} from './us-privacy-from-gpp';

/**
 * IAB consent readers shared by the ads stack (flint header bidding and
 * ads identity). The Sourcepoint CMP renders the __tcfapi/__uspapi/__gpp
 * stubs in the document head on every page (see
 * template/partials/external/sourcepoint.tsx); the stubs queue callbacks
 * until the CMP has loaded, so every read is wrapped in a short timeout to
 * guarantee the ad path is never stalled by a CMP that fails to load.
 */

// How long to wait on the CMP consent APIs before giving up (they answer
// synchronously once the CMP has loaded; the stubs queue callbacks until
// then).
export const CONSENT_TIMEOUT_MS = 300;

/**
 * Total budget for a CMP read that has to wait for its API to be installed:
 * the wait for the global to appear and the wait for it to answer share this,
 * they do not each get it.
 *
 * Google installs `__gpp` around 580-690ms after navigation and it reports
 * `signalStatus: "ready"` around 710-780ms (measured 2026-10-29), so a read
 * starting early needs several hundred ms. But `fetchFlintTargeting` aborts
 * the whole query at `FLINT_TIMEOUT_MS` (800ms) and returns no targeting at
 * all, so the two waits together must finish inside that with room for the
 * HTTP request itself. Budgeting them separately is how you accept a CMP that
 * goes ready at 850ms and then find flint already gave up: a missing GPP
 * string traded for a missing ad.
 */
export const CMP_READ_BUDGET_MS = 650;
const CMP_ARRIVAL_POLL_MS = 25;

export type TcData = {
  tcString?: string;
  gdprApplies?: boolean;
  purpose?: {
    consents?: Record<string, boolean>;
  };
};
export type UspData = { uspString?: string };
export type GppData = { gppString?: string; applicableSections?: number[] };

type GppPingData = GppData & { signalStatus?: string };

/**
 * TCData as delivered to an `addEventListener` callback. `eventStatus` says
 * whether the string is final: `tcloaded` and `useractioncomplete` carry a
 * decision, `cmpuishown` means the dialog is on screen and nothing has been
 * decided yet, so there is no string to read.
 */
type TcfEvent = TcData & { eventStatus?: string; listenerId?: number };

type GppEvent = {
  eventName?: string;
  listenerId?: number;
  pingData?: GppPingData;
};

type ConsentWindow = {
  __tcfapi?: (
    command: string,
    version: number,
    // `addEventListener` delivers TCData plus eventStatus/listenerId.
    callback: (data: TcData | TcfEvent | null, success: boolean) => void,
    // `removeEventListener` takes the listener id as a fourth argument.
    parameter?: unknown,
  ) => void;
  __uspapi?: (
    command: string,
    version: number,
    callback: (data: UspData | null, success: boolean) => void,
  ) => void;
  __gpp?: (
    command: string,
    callback: (data: GppPingData | GppEvent | null, success: boolean) => void,
    parameter?: unknown,
  ) => void;
};

const getConsentWindow = (): ConsentWindow =>
  getWindow() as unknown as ConsentWindow;

/**
 * Wait for a CMP global to exist before reading it.
 *
 * Google does not install its IAB APIs together. Measured against production
 * 2026-10-29: `__uspapi` appeared ~100ms after navigation, `__gpp` not until
 * ~580ms, reaching `signalStatus: "ready"` around 710-780ms. A read landing in
 * that gap took the `typeof api !== 'function'` branch and resolved null
 * *immediately*, spending none of its timeout and never seeing the CMP arrive.
 * Flint then sent `ccpa_strings` with no `gpp_strings`, so a GPP-only bidder
 * saw no opt-out at all.
 *
 * That branch was unreachable while our own IAB stubs defined the globals
 * synchronously; removing them in #4497 is what exposed it. Polling rather
 * than an event, because there is no event to subscribe to before the API
 * that would carry it exists.
 */
const awaitConsentApi = <T>(
  read: () => T | undefined,
  deadline: number,
): Promise<T | null> =>
  new Promise((resolve) => {
    if (typeof read() === 'function') {
      resolve(read() as T);
      return;
    }
    const tick = (): void => {
      const api = read();
      if (typeof api === 'function') {
        resolve(api);
      } else if (Date.now() >= deadline) {
        resolve(null);
      } else {
        setTimeout(tick, CMP_ARRIVAL_POLL_MS);
      }
    };
    setTimeout(tick, CMP_ARRIVAL_POLL_MS);
  });

// Resolve a CMP callback-style API with a timeout so a stub whose CMP never
// loads cannot stall the caller. The timer is cleared when the consent
// promise wins the race, so fast CMP responses don't leave timers queued.
const withConsentTimeout = <T>(
  promise: Promise<T | null>,
  deadline?: number,
): Promise<T | null> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  // A deadline shares one budget across the arrival wait and this one; without
  // it the caller gets the flat per-call timeout.
  const ms =
    deadline === undefined
      ? CONSENT_TIMEOUT_MS
      : Math.max(0, deadline - Date.now());
  return Promise.race([
    promise,
    new Promise<null>((resolve) => {
      timer = setTimeout(() => resolve(null), ms);
    }),
  ]).finally(() => {
    if (timer !== undefined) {
      clearTimeout(timer);
    }
  });
};

/**
 * Full IAB TCF consent data (GDPR), via the CMP's __tcfapi: the consent
 * string plus gdprApplies and per-purpose consents, for callers (ads
 * identity) that need more than the string itself. Resolves null when the
 * API never arrives or the CMP has not answered within the timeout. Waits for
 * `__tcfapi` to be installed for the same reason as {@link getGppConsent}.
 *
 * **Uses `addEventListener`, not `getTCData`.** `getTCData` was removed in TCF
 * 2.2 and Google Privacy & messaging (which reports `apiVersion: "2.3"`)
 * refuses it outright: measured against production from an EEA exit on
 * 2026-10-29, `getTCData` returned `success: false` while `addEventListener`
 * answered with `cmpId: 300` and `gdprApplies: true`. Since `getTcfConsentString`
 * is the only source of `gdpr_strings` for flint, calling the removed command
 * meant **no TCF signal on any EEA ad request, before or after consent** — not
 * a race, a permanent null. `gdpr-applies.js` had already been moved off
 * `getTCData` for this reason; this reader had not.
 */
export const getTcfConsentData = async (): Promise<TcData | null> => {
  const deadline = Date.now() + CMP_READ_BUDGET_MS;
  const api = await awaitConsentApi(
    () => getConsentWindow().__tcfapi,
    deadline,
  );
  if (!api) {
    return null;
  }
  // `settled` matters because registration can arrive *after* the deadline:
  // cleanup would then run with no listener id yet and leave the subscription
  // behind, one per timed-out read. A late registration unsubscribes itself.
  let settled = false;
  let listenerId: number | undefined;
  const removeListener = (): void => {
    if (listenerId === undefined) {
      return;
    }
    const id = listenerId;
    listenerId = undefined;
    try {
      // Read the API off the window again rather than closing over it: by now
      // the CMP may have replaced its own global.
      getConsentWindow().__tcfapi?.(
        'removeEventListener',
        2,
        () => undefined,
        id,
      );
    } catch (_) {
      // Inert either way once the promise has settled.
    }
  };
  return withConsentTimeout(
    new Promise<TcData | null>((resolve) => {
      try {
        api('addEventListener', 2, (data, success) => {
          if (!success || !data) {
            resolve(null);
            return;
          }
          const event = data as TcfEvent;
          if (event.listenerId !== undefined) {
            listenerId = event.listenerId;
            if (settled) {
              removeListener();
              return;
            }
          }
          // Outside the EEA, Google answers `gdprApplies: false` with no
          // `eventStatus` at all (measured from a Virginia exit 2026-10-29).
          // That is a final answer, so requiring an eventStatus would stall
          // until the deadline and hand back null, which makes
          // `isIdentityConsentGranted` fail closed and drops eids and PPID
          // for every US user.
          if (event.gdprApplies === false) {
            resolve(event);
            return;
          }
          // `cmpuishown` means the dialog is open and undecided: there is no
          // string yet, so keep listening rather than settling on an empty one.
          if (
            event.eventStatus === 'tcloaded' ||
            event.eventStatus === 'useractioncomplete'
          ) {
            resolve(event);
          }
        });
      } catch (_) {
        resolve(null);
      }
    }),
    deadline,
  ).finally(() => {
    // Runs on the resolved path and on the timeout. If registration has not
    // happened yet, the callback above removes it when it does.
    settled = true;
    removeListener();
  });
};

/** IAB TCF consent string (GDPR), via the CMP's __tcfapi. */
export const getTcfConsentString = (): Promise<string | null> =>
  getTcfConsentData().then((data) => data?.tcString || null);

const readUspApiOnce = (deadline?: number): Promise<string | null> =>
  withConsentTimeout(
    new Promise<string | null>((resolve) => {
      const api = getConsentWindow().__uspapi;
      if (typeof api !== 'function') {
        resolve(null);
        return;
      }
      try {
        api('getUSPData', 1, (data, success) => {
          resolve((success && data?.uspString) || null);
        });
      } catch (_) {
        resolve(null);
      }
    }),
    deadline,
  );

/**
 * The US Privacy string from `__uspapi`, waiting out an answer the CMP has
 * told us is provisional.
 *
 * Google installs `__uspapi` before its CCPA data has loaded and answers
 * `"1---"` until it has. Measured against production from a California exit
 * on 2026-10-29: `"1---"` at 558ms, `"1YNN"` at 797ms. `"1---"` asserts no
 * notice and no opt-out, so reading in that window reports a user who HAS
 * opted out as one who has not, on every bid request built from it.
 *
 * `__gpp` reports `signalStatus: "not ready"` for exactly that window, and is
 * the only signal available that the answer is provisional: the US Privacy
 * API has no readiness concept of its own, and Google's
 * `INITIAL_CCPA_DATA_READY` fires at ~1.5s on a cold load, roughly twice
 * {@link FLINT_TIMEOUT_MS}.
 *
 * **A missing `__gpp` means the answer stands.** On a warm reload Google
 * restores the decision and `__uspapi` is correct from 22ms, while `__gpp` is
 * not installed until 883ms and not ready until 1078ms. Waiting on it there
 * would spend the whole budget and discard a string that was right all along.
 *
 * Never worse than reading once: the provisional answer is kept, and returned
 * if the wait does not produce a better one inside the budget.
 */
const getUspApiString = async (deadline: number): Promise<string | null> => {
  const provisional = await readUspApiOnce(deadline);
  if (typeof getConsentWindow().__gpp !== 'function') {
    return provisional;
  }
  const ready = await getGppConsent(deadline);
  if (!ready) {
    return provisional;
  }
  return (await readUspApiOnce(deadline)) ?? provisional;
};

// getSection data is an array of subsection objects with the USNAT core
// first (GPP CMP API spec); some CMPs answer with the core object directly.
// Reading fields off the un-unwrapped array would default every signal to 0
// and fabricate "1---" over a real opt-out.
const unwrapUsnatSection = (data: unknown): UsnatFields | null => {
  const core = Array.isArray(data) ? data[0] : data;
  return core && typeof core === 'object' && !Array.isArray(core)
    ? (core as UsnatFields)
    : null;
};

/**
 * The USNAT section payload via a GPP CMP's getSection. The GPP spec does not
 * require the callback to run synchronously even after readiness, so this is
 * promise-shaped with the standard timeout. Exported with the api injected so
 * the synchronous, deferred, and array-shaped callback answers are all
 * unit-testable.
 */
export const getUsnatSectionViaApi = (
  api: NonNullable<ConsentWindow['__gpp']>,
  deadline?: number,
): Promise<UsnatFields | null> =>
  withConsentTimeout(
    new Promise<UsnatFields | null>((resolve) => {
      try {
        api(
          'getSection',
          (data, success) => {
            resolve(success ? unwrapUsnatSection(data) : null);
          },
          'usnat',
        );
      } catch (_) {
        resolve(null);
      }
    }),
    deadline,
  );

/**
 * IAB US Privacy string (CCPA). When `__uspapi` is installed its answer wins,
 * once {@link getUspApiString} has established it is not provisional. Only
 * when no `__uspapi` exists at all does this derive the legacy string from
 * the GPP USNAT section (decision D1a; mapping and conformance vectors in
 * us-privacy-from-gpp.ts).
 *
 * Both CMPs install `__uspapi`, so in practice the derivation is a backstop
 * rather than the Google path: Sourcepoint's inline stub renders it on every
 * page, and Google Privacy & messaging installs its own (measured 2026-10-29,
 * correcting an earlier claim that it serves none). It is reached when JS
 * externals are disabled, and it is what iOS, Android and flint rely on.
 *
 * Null means no legacy signal exists: either no US regime applies, or the
 * user is on a per-state GPP section, which must not be mapped.
 */
export const getUspConsentString = (): Promise<string | null> => {
  // One deadline for the whole read, whichever path it takes: waiting for
  // `__gpp` to arrive, then for it to report ready, then for `getSection` to
  // answer. Giving each step its own allowance is how three individually
  // reasonable timeouts add up to more than `FLINT_TIMEOUT_MS` and cost the
  // ad request entirely.
  const deadline = Date.now() + CMP_READ_BUDGET_MS;
  if (typeof getConsentWindow().__uspapi === 'function') {
    return getUspApiString(deadline);
  }
  return getGppConsent(deadline).then((gpp) => {
    if (!gpp?.applicableSections?.includes(GPP_SECTION_USNAT)) {
      return null;
    }
    const gppApi = getConsentWindow().__gpp;
    if (typeof gppApi !== 'function') {
      return null;
    }
    return getUsnatSectionViaApi(gppApi, deadline).then((fields) =>
      deriveUsPrivacyString(gpp.applicableSections, fields),
    );
  });
};

/**
 * IAB GPP string + applicable section ids, via the CMP's __gpp.
 *
 * Uses `ping` rather than `getGPPData`: the latter was dropped in GPP 1.1,
 * which is the version our injected stub declares, so a conforming GPP-only
 * CMP need not answer it. `ping` is required in every version and its reply
 * carries both fields.
 *
 * Resolves null unless `signalStatus` is "ready". An unready CMP reports an
 * empty `applicableSections`, indistinguishable from a genuine rest-of-world
 * answer, so null means "not established" and callers fail closed on it.
 *
 * Two waits, deliberately separate. {@link awaitConsentApi} waits for `__gpp`
 * to be installed at all, because Google adds it several hundred ms after
 * `__uspapi`. A not-ready ping is then not settled on either: this registers
 * a GPP event listener and resolves on the first event carrying a ready
 * `pingData`, with `withConsentTimeout` bounding that second wait. The
 * listener is always removed, on the ready path and on the timeout.
 */
export const getGppConsent = async (
  deadline: number = Date.now() + CMP_READ_BUDGET_MS,
): Promise<GppData | null> => {
  let cleanup = (): void => undefined;
  const api = await awaitConsentApi(() => getConsentWindow().__gpp, deadline);
  if (!api) {
    return null;
  }
  return withConsentTimeout(
    new Promise<GppData | null>((resolve) => {
      const toResult = (data: GppPingData): GppData => ({
        gppString: data.gppString,
        applicableSections: data.applicableSections,
      });
      try {
        api('ping', (data, success) => {
          const ping = (data || undefined) as GppPingData | undefined;
          if (success && ping?.signalStatus === 'ready') {
            resolve(toResult(ping));
            return;
          }
          // Settling on a not-ready ping would spend none of the timeout
          // waiting for the real answer. Listen for readiness instead.
          let listenerId: number | undefined;
          cleanup = () => {
            if (listenerId === undefined) {
              return;
            }
            const id = listenerId;
            listenerId = undefined;
            try {
              api('removeEventListener', () => undefined, id);
            } catch (_) {
              // Inert either way once the promise has settled.
            }
          };
          api('addEventListener', (evt, ok) => {
            if (!ok || !evt) {
              resolve(null);
              return;
            }
            const event = evt as GppEvent;
            if (event.eventName === 'listenerRegistered') {
              listenerId = event.listenerId;
            }
            if (event.pingData?.signalStatus === 'ready') {
              resolve(toResult(event.pingData));
            }
          });
        });
      } catch (_) {
        resolve(null);
      }
    }),
    deadline,
  ).finally(() => {
    // Runs on the ready path and on the timeout, so the listener never leaks.
    cleanup();
  });
};
